import type {
  DomainEventFrame,
  DomainEventGroupKind,
  DomainEventGroupMember,
} from '#entities/DomainEvent';

export interface CaseTimelineRawEvent {
  seq: string;
  type: string;
  eventId: string;
  occurredAt: string;
  observedAt: string;
  at: string;
  payload: unknown;
  previous?: Record<string, unknown>;
}

export interface CaseAirLeg {
  legId: string;
  maoTripId: number;
  maoLegId: number;
  departureIcao: string | null;
  arrivalIcao: string | null;
  plannedOffBlocksUtc: string | null;
  plannedOnBlocksUtc: string | null;
  actualTakeOffUtc: string | null;
  actualLandingUtc: string | null;
}

export const APPROVAL_SLOT_KEYS = [
  'serviceAcceptance',
  'aviation',
  'quoteInternal',
  'quoteCustomer',
  'organAcceptance',
] as const;

export type ApprovalSlotKey = (typeof APPROVAL_SLOT_KEYS)[number];

export type ApprovalSlotState =
  'absent' | 'outstanding' | 'approved' | 'declined';

export interface ApprovalSlot {
  key: ApprovalSlotKey;
  state: ApprovalSlotState;
  requestedAt: string | null;
  answeredAt: string | null;
}

export type OrganAcceptanceOutcome = 'accepted' | 'declined';

export interface OrganAcceptanceRound {
  requestedAt: string;
  answeredAt: string | null;
  outcome: OrganAcceptanceOutcome | null;
}

export interface CaseApprovals {
  /** Always five, in `APPROVAL_SLOT_KEYS` order. */
  slots: ApprovalSlot[];
  organRounds: OrganAcceptanceRound[];
}

export interface CaseTimelineCaseData {
  caseId: string;
  caseLabel: string | null;
  frames: DomainEventFrame[];
  events: CaseTimelineRawEvent[];
  airLegs: CaseAirLeg[];
  approvals: CaseApprovals;
}

export interface CasesTimeline {
  latestSeq: string | null;
  cases: CaseTimelineCaseData[];
}

export interface CaseTimelineResult {
  latestSeq: string | null;
  case: CaseTimelineCaseData | null;
}

export interface CasesTimelineArgs {
  sinceSeq?: string;
}

export interface CaseTimelineArgs {
  caseId: string;
}

export type TimelineCheckpointState =
  | 'planned'
  | 'due'
  | 'done'
  | 'slipped'
  | 'unfulfillable'
  | 'superseded'
  | 'cancelled';

export type ProcessOwner =
  | 'Aviation'
  | 'Ground'
  | 'Clinical'
  | 'Device/OCS'
  | 'Finance'
  | 'Planning and approvals'
  | 'Resources'
  | 'Context';

export type RouteLegKind = 'air' | 'ground';

export type OrganAcceptanceCheckpointType =
  'organAcceptanceRequested' | 'organAcceptanceAnswered';

export type TimelineCheckpointType =
  DomainEventGroupKind | OrganAcceptanceCheckpointType | 'airLeg' | 'groundLeg';

export interface TimelineCheckpoint {
  id: string;
  /** A group checkpoint's group key. A leg checkpoint's first merged group key when a group merged into the leg, else its leg id. */
  key?: string;
  type: TimelineCheckpointType;
  owner: ProcessOwner;
  plannedAt?: string;
  actualAt?: string;
  state: TimelineCheckpointState;
  /** On an answered organ-acceptance checkpoint: how long the answer took after the request. */
  answeredInMs?: number;
  events: readonly DomainEventGroupMember[];
}

export interface CaseTimeline {
  caseId: string;
  caseLabel: string | null;
  checkpoints: TimelineCheckpoint[];
  approvals: CaseApprovals;
}

export interface CaseRouteProjectionLeg {
  legId: string;
  kind: RouteLegKind;
  /** The NOP route segment the leg serves, such as `DonorHospitalToTxCenter`. */
  segmentType: string | null;
  departureTime: string | null;
  arrivalTime: string | null;
}

/** The route projection in force: its Main-route air and ground legs. */
export interface CaseRouteProjection {
  seq: string;
  recordedAt: string;
  legs: CaseRouteProjectionLeg[];
}
